import React, { useState } from 'react';
import { Trash2, Sparkles, Users, Bookmark } from 'lucide-react';
import { useTheme } from '../core/ui/ThemeContext';
import { useLanguage } from '../core/i18n/LanguageContext';
import CartHeader from './cart/CartHeader';
import CartEmptyState from './cart/CartEmptyState';
import CartItem from './cart/CartItem';
import CartSummary from './cart/CartSummary';
import SavedCartsPanel from './cart/SavedCartsPanel';
import { formatEUR, formatNumberDE } from 'core/utils/format';

const CartView = ({
  cart,
  updateCartQuantity,
  getCartTotal,
  onBack,
  createPurchaseRequisition,
  ociCheckout,
  ociMode,
  ociParams,
  user,
  onEditItem,
  showSuccess,
  showError,
  onApplyKontierungToAll,
  savedCarts,
  onOpenSaveCartModal,
  onLoadSavedCart,
  onRenameSavedCart,
  onDeleteSavedCart,
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [savingFavorite, setSavingFavorite] = useState(false);

  // Debug: OCI-Parameter ausgeben
  console.log('🔍 CartView Debug:', {
    ociMode,
    ociParams,
    hookUrl: ociParams?.hookUrl,
    hasOciParams: !!ociParams,
    userRole: user?.role,
    cartLength: cart?.length || 0
  });

  // Debug: Komponente wird gerendert
  console.log('🛒 CartView wird gerendert mit', cart?.length || 0, 'Artikeln');

  const handleEditItem = (item) => {
    if (onEditItem) {
      onEditItem(item);
    }
  };

  // Add item to favorites
  const handleAddToFavorites = async (item) => {
    if (!user?.token) {
      showError?.(t('cart.loginForFavorites'));
      return;
    }

    setSavingFavorite(true);
    try {
      const response = await fetch('/api/favorites', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          name: item.description || item.name || 'Unbenannter Artikel',
          description: item.longDescription || item.description || '',
          quantity: item.quantity || 1,
          unit: item.unit || 'ST',
          price: item.price,
          currency: item.currency || 'EUR',
          supplier: item.supplier || item.vendorName,
          supplier_id: item.supplierId || item.vendorId,
          material_number: item.materialNumber || item.matnr,
          product_group: item.productGroup || item.matgroup,
          cost_center: item.cost_center,
          plant: item.plant,
          storage_location: item.storage_location,
          gl_account: item.gl_account,
          order_number: item.order_number,
          wbs_element: item.wbs_element,
          asset_number: item.asset_number,
          network_number: item.network_number,
          preferred_vendor: item.preferred_vendor,
          manufacturer: item.manufacturer,
          manufacturer_part_number: item.manufacturer_part_number,
          einkauefergruppe: item.einkauefergruppe,
          einkaueferorganisation: item.einkaueferorganisation,
          anforderer: item.anforderer,
          kontierungstyp: item.kontierungstyp || 'kostenstelle',
          item_data: item // Store the complete item data
        })
      });

      if (response.ok) {
        showSuccess?.(t('cart.addedToFavorites'));
      } else {
        const error = await response.json();
        showError?.(error.message || t('cart.errorSavingFavorite'));
      }
    } catch (error) {
      console.error('Error saving favorite:', error);
      showError?.(t('cart.errorSavingFavorite'));
    } finally {
      setSavingFavorite(false);
    }
  };

  const formatPrice = (price, currency) => formatEUR(price, currency);

  const calculateItemTotal = (price, quantity) => {
    const numPrice = parseFloat(price) || 0;
    const numQuantity = parseFloat(quantity) || 0;
    return formatNumberDE(numPrice * numQuantity);
  };

  // Selection handlers
  const toggleItemSelection = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const selectAll = () => {
    const allIds = new Set(cart.map(item => item.id));
    setSelectedItems(allIds);
  };

  const selectNone = () => {
    setSelectedItems(new Set());
  };

  // Calculate selected items total
  const getSelectedTotal = () => {
    return cart
      .filter(item => selectedItems.has(item.id))
      .reduce((total, item) => {
        return total + (item.price * item.quantity);
      }, 0).toFixed(2);
  };

  const hasSelection = selectedItems.size > 0;

  // Get selected items
  const getSelectedItems = () => {
    return cart.filter(item => selectedItems.has(item.id));
  };

  // Delete selected items
  const deleteSelectedItems = async () => {
    if (selectedItems.size === 0) return;

    const count = selectedItems.size;
    for (const itemId of selectedItems) {
      await updateCartQuantity(itemId, 0);
    }
    setSelectedItems(new Set());
    showSuccess?.(t('cart.removedItems', { count }));
  };

  return (
    <div className={`${isDarkMode
      ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-black'
      : 'bg-white'
    }`}>
      <CartHeader
        cart={cart}
        getCartTotal={getCartTotal}
        onBack={onBack}
        formatPrice={formatPrice}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {cart.length === 0 ? (
          <CartEmptyState />
        ) : (
          <>
            {/* KI-Prüfungs-Hinweis (Demo) */}
            <div className={`mb-4 flex items-start gap-3 px-4 py-3 rounded-xl border ${
              isDarkMode
                ? 'bg-blue-900/10 border-blue-400/20'
                : 'bg-brand/5 border-brand/15'
            }`}>
              <Sparkles className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isDarkMode ? 'text-blue-400' : 'text-brand'}`} />
              <div className="flex-1 min-w-0">
                <span className={`text-sm font-medium ${isDarkMode ? 'text-blue-300' : 'text-brand'}`}>
                  {t('cart.aiCheckTitle')}
                </span>
                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-blue-300/70' : 'text-brand/70'}`}>
                  {t('cart.aiCheckDesc')}
                </p>
              </div>
            </div>

            {/* Selection Toolbar - above both columns */}
            <div className={`flex flex-wrap items-center gap-2 mb-4 px-4 py-2.5 rounded-2xl ${
              isDarkMode
                ? 'bg-gray-800/30 border border-white/10'
                : 'bg-white border border-slate-200'
            }`}>
              <button
                onClick={selectAll}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isDarkMode
                    ? 'bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 border border-blue-500/30'
                    : 'bg-brand/10 hover:bg-brand/20 text-brand border border-brand/30'
                }`}
              >
                {t('cart.selectAll')}
              </button>
              <button
                onClick={selectNone}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isDarkMode
                    ? 'bg-gray-600/20 hover:bg-gray-600/30 text-gray-300 border border-gray-500/30'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200'
                }`}
              >
                {t('cart.deselectAll')}
              </button>

              <span className={`hidden sm:block w-px h-6 self-center ${isDarkMode ? 'bg-white/10' : 'bg-slate-200'}`}></span>

              {cart.length > 1 && onApplyKontierungToAll && (
                <button
                  onClick={() => onApplyKontierungToAll(hasSelection ? getSelectedItems() : null)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all duration-200 ${
                    isDarkMode
                      ? 'bg-blue-600/15 hover:bg-blue-600/25 text-blue-300 border-blue-500/30'
                      : 'bg-brand/10 hover:bg-brand/15 text-brand border-brand/25'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  {hasSelection ? t('cart.kontForSelected', { count: selectedItems.size }) : t('cart.kontForAll')}
                </button>
              )}
              {typeof onOpenSaveCartModal === 'function' && (
                <button
                  onClick={() => onOpenSaveCartModal(hasSelection ? getSelectedItems() : null)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all duration-200 ${
                    isDarkMode
                      ? 'bg-brand/15 hover:bg-brand/25 text-blue-300 border-brand/30'
                      : 'bg-brand/10 hover:bg-brand/15 text-brand border-brand/25'
                  }`}
                >
                  <Bookmark className="w-4 h-4" />
                  {hasSelection ? t('savedCarts.saveSelectedBtn', { count: selectedItems.size }) : t('savedCarts.saveCartBtn')}
                </button>
              )}
              {selectedItems.size > 0 && (
                <button
                  onClick={deleteSelectedItems}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isDarkMode
                      ? 'bg-red-600/20 hover:bg-red-600/30 text-red-300 border border-red-500/30'
                      : 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-200'
                  }`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {t('cart.deleteCount', { count: selectedItems.size })}
                </button>
              )}

              <span className={`ml-auto text-sm ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                {t('cart.selectedOf', { selected: selectedItems.size, total: cart.length })}
              </span>
            </div>

            {/* Gespeicherte Warenkörbe (oben, einklappbar) */}
            {Array.isArray(savedCarts) && (
              <div className="mb-4">
                <SavedCartsPanel
                  savedCarts={savedCarts}
                  onLoad={typeof onLoadSavedCart === 'function' ? onLoadSavedCart : () => {}}
                  onRename={typeof onRenameSavedCart === 'function' ? onRenameSavedCart : () => {}}
                  onDelete={typeof onDeleteSavedCart === 'function' ? onDeleteSavedCart : () => {}}
                  collapsible={true}
                  defaultOpen={false}
                />
              </div>
            )}

            <div className="flex flex-col lg:flex-row gap-6">
              {/* Cart Items */}
              <div className="flex-1 min-w-0">
                <div className="space-y-3">
                  {Array.isArray(cart) && cart.map((item, index) => (
                    <CartItem
                      key={item.id}
                      item={item}
                      index={index}
                      isSelected={selectedItems.has(item.id)}
                      onToggleSelection={toggleItemSelection}
                      onEditItem={handleEditItem}
                      updateCartQuantity={updateCartQuantity}
                      formatPrice={formatPrice}
                      calculateItemTotal={calculateItemTotal}
                      user={user}
                      onAddToFavorites={handleAddToFavorites}
                    />
                  ))}
                </div>
              </div>

              <CartSummary
                cart={cart}
                selectedItems={getSelectedItems()}
                getCartTotal={getCartTotal}
                getSelectedTotal={getSelectedTotal}
                formatPrice={formatPrice}
                ociMode={ociMode}
                ociParams={ociParams}
                ociCheckout={ociCheckout}
                createPurchaseRequisition={createPurchaseRequisition}
                user={user}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CartView;
